import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ProjetoDaBarra } from "@base/ui";
import { useAuth } from "../auth/auth.contexto";
import { useClientesGateway } from "../../modules/clientes/application/clientes.contexto";
import { avisar } from "../ui/dialogos-api";

const CHAVE = "renke-refs.projeto-ativo";
const chaveLista = ["clientes", "lista"] as const;

type EstadoProjeto = {
  readonly projetos: readonly ProjetoDaBarra[];
  readonly projetoAtivoId: string;
  readonly carregando: boolean;
  readonly definirProjetoAtivo: (id: string) => Promise<void>;
  readonly abrirCriacao: () => void;
  readonly fecharCriacao: () => void;
  readonly criacaoAberta: boolean;
  readonly irParaConfiguracao: () => void;
  readonly excluirProjetoAtivo: () => Promise<void>;
  readonly recarregarLista: () => Promise<void>;
};

const Contexto = createContext<EstadoProjeto | null>(null);

function lerLembrete(): string {
  try {
    return globalThis.localStorage?.getItem(CHAVE) ?? "";
  } catch {
    return "";
  }
}

function gravarLembrete(id: string): void {
  try {
    globalThis.localStorage?.setItem(CHAVE, id);
  } catch {
    // ignore
  }
}

/**
 * Workspaces = clientes do usuario (memberships).
 * Fonte da verdade: sessao.tenantId. Troca de workspace rebinda a sessao e
 * recarrega a tela para nao misturar dados entre projetos.
 */
export function ProvedorDeProjeto({ children }: { readonly children: ReactNode }) {
  const { sessao, trocarTenant } = useAuth();
  const gateway = useClientesGateway();
  const queryClient = useQueryClient();
  const [criacaoAberta, definirCriacaoAberta] = useState(false);
  const sincronizandoBoot = useRef(false);

  const lista = useQuery({
    queryKey: chaveLista,
    queryFn: () => gateway.listar(),
    enabled: Boolean(sessao),
  });

  const projetos = useMemo<readonly ProjetoDaBarra[]>(() => {
    const itens = lista.data?.itens ?? [];
    if (itens.length > 0) {
      return itens.map((c) => ({
        id: c.id,
        nome: c.nome,
        plano: "Workspace",
      }));
    }
    if (!sessao) return [];
    return [
      {
        id: sessao.tenantId,
        nome: sessao.cliente,
        plano: sessao.email ?? "Workspace",
      },
    ];
  }, [lista.data, sessao]);

  // Sessao manda: o seletor espelha o tenantId do token.
  const ativoResolvido = sessao?.tenantId ?? projetos[0]?.id ?? "";

  // Boot: se o lembrete for um workspace valido e diferente da sessao, troca
  // e recarrega. Se for invalido, alinha o storage com a sessao.
  useEffect(() => {
    if (!sessao || lista.isPending || sincronizandoBoot.current) return;
    const lembrete = lerLembrete();
    if (!lembrete || lembrete === sessao.tenantId) {
      if (sessao.tenantId) gravarLembrete(sessao.tenantId);
      return;
    }
    const existe = projetos.some((p) => p.id === lembrete);
    if (!existe) {
      gravarLembrete(sessao.tenantId);
      return;
    }
    sincronizandoBoot.current = true;
    void (async () => {
      try {
        await trocarTenant(lembrete);
        gravarLembrete(lembrete);
        globalThis.location.reload();
      } catch {
        gravarLembrete(sessao.tenantId);
        sincronizandoBoot.current = false;
      }
    })();
  }, [sessao, lista.isPending, projetos, trocarTenant]);

  const definirProjetoAtivo = useCallback(
    async (id: string) => {
      if (!id || id === sessao?.tenantId) {
        if (id) gravarLembrete(id);
        return;
      }
      await trocarTenant(id);
      gravarLembrete(id);
      // Reload garante que midias, posts e caches do React Query nao
      // misturem dados do workspace anterior.
      globalThis.location.reload();
    },
    [sessao?.tenantId, trocarTenant],
  );

  const recarregarLista = useCallback(async () => {
    await lista.refetch();
  }, [lista]);

  const excluirProjetoAtivo = useCallback(async () => {
    const id = ativoResolvido;
    if (!id) return;
    if (id === "a0000000-0000-4000-8000-000000000001") {
      await avisar({
        titulo: "Projeto protegido",
        descricao:
          "O projeto da conta (Renke Studio) não pode ser excluído.",
      });
      return;
    }
    if (projetos.length <= 1) {
      await avisar({
        titulo: "Único projeto",
        descricao:
          "Não dá para excluir o único projeto da conta. Crie outro antes.",
      });
      return;
    }

    await gateway.atualizarStatus(id, "inativo");
    const restante = projetos.find((p) => p.id !== id);
    await queryClient.invalidateQueries({ queryKey: chaveLista });
    await lista.refetch();
    if (restante) {
      await definirProjetoAtivo(restante.id);
    }
  }, [
    ativoResolvido,
    definirProjetoAtivo,
    gateway,
    lista,
    projetos,
    queryClient,
  ]);

  const valor = useMemo(
    () => ({
      projetos,
      projetoAtivoId: ativoResolvido,
      carregando: lista.isPending,
      definirProjetoAtivo,
      abrirCriacao: () => definirCriacaoAberta(true),
      fecharCriacao: () => definirCriacaoAberta(false),
      criacaoAberta,
      irParaConfiguracao: () => {
        window.location.hash = "configuracoes";
      },
      excluirProjetoAtivo,
      recarregarLista,
    }),
    [
      projetos,
      ativoResolvido,
      lista.isPending,
      definirProjetoAtivo,
      criacaoAberta,
      excluirProjetoAtivo,
      recarregarLista,
    ],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useProjetoAtivo(): EstadoProjeto {
  const ctx = useContext(Contexto);
  if (!ctx) {
    throw new Error("useProjetoAtivo precisa de ProvedorDeProjeto.");
  }
  return ctx;
}

/** Props prontas para `MolduraDoApp` (seletor no topo). */
export function usePropsDoSeletorNaMoldura(): {
  readonly projetos: readonly ProjetoDaBarra[];
  readonly projetoAtivoId: string;
  readonly onMudarProjeto: (id: string) => void;
  readonly rotuloListaProjetos: string;
  readonly onCriarProjeto: () => void;
  readonly rotuloCriarProjeto: string;
} {
  const { projetos, projetoAtivoId, definirProjetoAtivo, abrirCriacao } =
    useProjetoAtivo();
  return {
    projetos,
    projetoAtivoId,
    onMudarProjeto: (id) => {
      void definirProjetoAtivo(id);
    },
    rotuloListaProjetos: "Projetos",
    onCriarProjeto: abrirCriacao,
    rotuloCriarProjeto: "Novo projeto",
  };
}
